import { 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  Query
} from "firebase/firestore";
import { db } from "../../../config/firebase-config";
import { ProfileSummary } from "../../../models";

/**
 * Sequential Tier Profile Paginator
 * Fetches ALL premium profiles first, then non-premium
 * First page: 5 items | Subsequent pages: 6 items
 */
export class ProfileSequentialTierPaginator {
  private collection: string = "creator-profiles-summary";
  private premiumLastVisible: any = null;
  private nonPremiumLastVisible: any = null;
  private premiumExhausted: boolean = false;
  private nonPremiumExhausted: boolean = false;
  private isFirstPage: boolean = true;
  private occupationFilter?: string;

  /**
   * Fetch combined profiles with sequential tier ordering
   * First page: 5 items | Subsequent: 6 items
   */
  async fetchCombined(
    occupationFilter?: string
  ): Promise<{ items: ProfileSummary[], hasMore: boolean }> {
    try {
      this.occupationFilter = occupationFilter;
      console.log("🔄 fetchCombined called. isFirstPage:", this.isFirstPage);

      // Determine fetch limit: 5 on first page, 6 on subsequent
      const fetchLimit = this.isFirstPage ? 5 : 6;
      const items: ProfileSummary[] = [];

      // Step 1: Fetch premium profiles (unless premium is exhausted)
      if (!this.premiumExhausted) {
        console.log("📋 Fetching premium tier, limit:", fetchLimit);
        const premiumItems = await this.fetchFromTier(true, fetchLimit);
        console.log("✅ Premium items returned:", premiumItems.length);
        items.push(...premiumItems);

        // Check if premium tier is exhausted (got less than requested)
        if (premiumItems.length < fetchLimit) {
          this.premiumExhausted = true;
          console.log("⚠️ Premium tier exhausted");
        }
      }

      // Step 2: Fill remaining slots with non-premium (if needed and available)
      if (items.length < fetchLimit && !this.nonPremiumExhausted) {
        const nonPremiumNeeded = fetchLimit - items.length;
        console.log("📋 Fetching non-premium tier to fill, need:", nonPremiumNeeded);
        const nonPremiumItems = await this.fetchFromTier(false, nonPremiumNeeded);
        console.log("✅ Non-premium items returned:", nonPremiumItems.length);
        items.push(...nonPremiumItems);

        // Check if non-premium tier is exhausted
        if (nonPremiumItems.length < nonPremiumNeeded) {
          this.nonPremiumExhausted = true;
          console.log("⚠️ Non-premium tier exhausted");
        }
      }

      // hasMore is true if we got the full limit (means there are more to fetch)
      const hasMore = items.length === fetchLimit;
      console.log("📊 Total items:", items.length, "hasMore:", hasMore, "premiumExhausted:", this.premiumExhausted, "nonPremiumExhausted:", this.nonPremiumExhausted);
      this.isFirstPage = false;

      return { items, hasMore };
    } catch (error) {
      console.error("Error fetching combined profiles:", error);
      return { items: [], hasMore: false };
    }
  }

  /**
   * Fetch from a specific tier (premium or non-premium)
   * Now optimized: All profiles have isSubscribed field, so we use direct Firestore queries
   */
  private async fetchFromTier(
    isPremium: boolean,
    fetchLimit: number
  ): Promise<ProfileSummary[]> {
    try {
      console.log(`🔍 fetchFromTier: isPremium=${isPremium}, limit=${fetchLimit}`);
      
      // Get the appropriate lastVisible for this tier
      const lastVisible = isPremium 
        ? this.premiumLastVisible 
        : this.nonPremiumLastVisible;

      // Build query constraints with direct filtering
      const queryConstraints: any[] = [
        where("isSubscribed", "==", isPremium),
        orderBy("creationTime", "desc")
      ];

      // Add occupation filter if provided
      if (this.occupationFilter && this.occupationFilter !== "all") {
        queryConstraints.push(where("occupation", "==", this.occupationFilter));
      }

      // Add pagination
      if (lastVisible) {
        queryConstraints.push(startAfter(lastVisible));
      }

      queryConstraints.push(limit(fetchLimit));

      console.log(`🔍 Query: isSubscribed=${isPremium}, occupation=${this.occupationFilter}, hasCursor=${!!lastVisible}`);

      const q = query(collection(db, this.collection), ...queryConstraints);
      const snapshot = await getDocs(q);
      
      console.log(`📦 Firestore returned ${snapshot.docs.length} documents for isPremium=${isPremium}`);
      
      const items: ProfileSummary[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        console.log(`📄 Doc ${doc.id}: isSubscribed=${data.isSubscribed}`);
        items.push({
          ...data,
          userId: doc.id,
          isSubscribed: data.isSubscribed
        } as ProfileSummary);
      });

      // Update the lastVisible for this tier
      if (items.length > 0) {
        const newLastVisible = snapshot.docs[snapshot.docs.length - 1];
        if (isPremium) {
          this.premiumLastVisible = newLastVisible;
        } else {
          this.nonPremiumLastVisible = newLastVisible;
        }
      }

      return items;
    } catch (error) {
      console.error(`Error fetching ${isPremium ? 'premium' : 'non-premium'} profiles:`, error);
      return [];
    }
  }

  /**
   * Reset pagination to initial state
   */
  reset() {
    this.premiumLastVisible = null;
    this.nonPremiumLastVisible = null;
    this.premiumExhausted = false;
    this.nonPremiumExhausted = false;
    this.isFirstPage = true;
  }
}
